import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { URL } from './../../configs/config';
import { session } from '../session/session';
import { constants } from '../../configs/config-internal-constants';
import { applicationContentConfig } from '../../configs/config-internal-content';
import { map } from 'rxjs/operators';
import * as uuid from 'uuid';
import { BrandService } from './brand-service';
import { DOCUMENT } from '@angular/common';
import { IAccessTokenResponse } from '../models/token.model';
import { BehaviorSubject } from 'rxjs';
import { CommonUtilities } from 'src/app/shared/util/common-utilities';

@Injectable({
  providedIn: 'root',
})
export class StartupService {
  /* tslint:disable:variable-name */
  private _startupData: any = null;
  /* tslint:disable:variable-name */

  private userAuthenticated = new BehaviorSubject(false);
  faviconElement: HTMLElement | null;

  constructor(
    public http: HttpClient,
    private brandService: BrandService,
    @Inject(DOCUMENT) private document: any
  ) {
    this.faviconElement = this.document.getElementById('appFavicon');
  }

  // This is the method you want to call at bootstrap
  // Important: It should return a Promise
  load(): Promise<any> {
    return this.http
      .get('config.json')
      .toPromise()
      .then((data: any) => {
        this._startupData = data;
        const headerJson = {
          'X-API-VERSION': '2',
          scopes: 'payments.read',
          'operationType': constants.V10
        };
        const httpOptions = {
          headers: new HttpHeaders(headerJson),
          withCredentials: true,
        };

        if (!this.brandService.isCahoot()) {
          if (this.faviconElement) {
            this.faviconElement.setAttribute('href', './assets/favicon.ico');
          }
          return this.http
            .get(URL.host + URL.authorizeEndpoint, httpOptions)
            .pipe(
              map((response: IAccessTokenResponse) => {
                this.loadIsCahootFalse(response);
              })
            )
            .toPromise()
            .catch((err: any) => {
              this.userAuthenticated.next(false);
              this.logout(applicationContentConfig.externalSantanderLogoff);
              Promise.resolve();
            });
        } else {
          if (this.faviconElement) {
            this.faviconElement.setAttribute(
              'href',
              './assets/favicon-cahoot.ico'
            );
          }
          return this.http
            .get(URL.host + URL.authorizeEndpoint, httpOptions)
            .pipe(
              map((response: IAccessTokenResponse) => {
                this.loadIsCahootTrue(response);
              })
            )
            .toPromise()
            .catch((err: any) => {
              this.userAuthenticated.next(false);
              this.logout(applicationContentConfig.externalCahootLogOffUrl);
              Promise.resolve();
            });
        }
      })
      .catch((err: any) => {
        if (!this.brandService.isCahoot()) {
          this.logout(applicationContentConfig.externalSantanderLogoff);
        } else {
          this.logout(applicationContentConfig.externalCahootLogOffUrl);
        }
        Promise.resolve();
      });
  }

  loadIsCahootTrue(response) {
    if (response && response.accessToken) {
      this.userAuthenticated.next(true);
      this.createCustomerSessionId();
      const headerJsonCahoot = {
        'X-API-VERSION': constants.XAPIVERSION,
        Authorization: `Bearer ${response['accessToken']}`,
        'operationType': constants.V10
      };
      const httpOptionsCahoot = {
        headers: new HttpHeaders(headerJsonCahoot),
        withCredentials: true,
      };
      this.http
        .post(
          this.host + this.revokeEndpoint,
          null,
          httpOptionsCahoot
        )
        .subscribe();
    } else {
      this.userAuthenticated.next(false);
      this.logout(applicationContentConfig.externalCahootLogOffUrl);
    }
  }

  loadIsCahootFalse(response) {
    if (response && response.accessToken) {
      this.userAuthenticated.next(true);
      session.uid = response['uid'];
      session.accessToken = response['accessToken'];
      this.setBrandInSession();
      this.createCustomerSessionId();
      const headerJsonRetail = {
        'X-API-VERSION': constants.XAPIVERSION,
        Authorization: `Bearer ${response['accessToken']}`,
        'operationType': constants.V10
      };
      const httpOptionsRetail = {
        headers: new HttpHeaders(headerJsonRetail),
        withCredentials: true,
      };
      this.http
        .post(this.host + this.revokeEndpoint, null, httpOptionsRetail)
        .subscribe();
    } else {
      this.userAuthenticated.next(false);
      this.logout(applicationContentConfig.externalSantanderLogoff);
    }
  }

  setBrandInSession() {
    for (const entry of this._startupData.retailDomains) {
      if (window.location.hostname.indexOf(entry) !== -1) {
        session.brand = constants.RETAIL;
      }
    }
  }

  logout(redirectUrl: string) {
    let domain = '/';
    const arr = window.location.hostname.split('.');
    if (arr.length > 3) {
      domain =
        arr[arr.length - 3] +
        '.' +
        arr[arr.length - 2] +
        '.' +
        arr[arr.length - 1];
    } else {
      domain = window.location.hostname;
    }
    const url =
      this.host +
      applicationContentConfig.revokeSessionUrl.replace('{{domain}}', domain);
    return this.http.get<any>(url).subscribe(
      (value) => {
        this.navigateByUrl(redirectUrl);
      },
      (error) => {
        this.navigateByUrl(redirectUrl);
      }
    );
  }

  navigateByUrl(url): void {
    window.location.assign(url);
  }

  createCustomerSessionId() {
    const randomArray = new Uint8Array(16);
    const rngType = window.crypto ? 'crypto' : 'msCrypto';
    window[rngType].getRandomValues(randomArray);
    const v4options = { random: randomArray };
    const sessionId = uuid.v4(v4options).replace(/-/g, '');
    if (sessionId && sessionId.length <= 32) {
      session.customerSessionId = sessionId;
    }
  }

  isAuthenticated() {
    return this.userAuthenticated.asObservable();
  }

  get externalSantander(): string {
    return URL.externalSantander;
  }

  get host(): string {
    return URL.host;
  }

  get startupData(): any {
    return this._startupData;
  }

  get apiClientId(): string {
    let isPilot = false;
    for (const entry of constants.pilotDomains) {
      if (window.location.hostname.indexOf(entry) !== -1) {
        isPilot = true;
      }
    }

    if(isPilot && this.startupData.apiTestClientId){
        return this.startupData.apiTestClientId;
    }
    return this.startupData.apiClientId;
  }

  get eventTrackerURL(): string {
    return this.startupData.eventTrackerURL;
  }
  
  get authorizeEndpoint(): string {
    return URL.authorizeEndpoint;
  }

  get revokeEndpoint(): string {
    return URL.revokeEndpoint;
  }

  get customerProfile(): string {
    return URL.customerProfileUrl;
  }

  get adobeTrackingURL(): string {
    return this.startupData.adobeTrackingURL;
  }

  get externalCahoot(): string {
    return URL.externalCahoot;
  }

  get sendSecureMessage(): string {
    return URL.sendSecureMessageUrl;
  }

  get externalSecureCahoot(): string {
    return URL.externalSecureCahoot;
  }

  get chatWithUs(): string {
    if (constants.shadowDomains.indexOf(location.hostname) >= 0) {
      return this.startupData.chatWithUsShadow;
    } else {
      return this.startupData.chatWithUs;
    }
  }

  get lostAndStolenCards(): string {
    if (constants.shadowDomains.indexOf(location.hostname) >= 0) {
      return this.startupData.lostAndStolenCardsShadow;
    } else {
      return this.startupData.lostAndStolenCards;
    }
  }

  get closeMyAccount(): string {
    if (constants.shadowDomains.indexOf(location.hostname) >= 0) {
      return this.startupData.closeMyAccountShadow;
    } else {
      return this.startupData.closeMyAccount;
    }
  }

  get autoconfigURL(): string {
    if (constants.shadowDomains.indexOf(location.hostname) >= 0) {
      return this.startupData.autoconfigURLShadow;
    } else {
      return this.startupData.autoconfigURL;
    }
  }

  get tealiumLogonUrl(): string {
    return this.startupData.tealiumLogonUrl;
  }

  getIsCahoot(): boolean {
    // check whether application is of type Cahoot
    return CommonUtilities.isBrand(constants.BRAND_CAHOOT);
  }

  get launchDarklyClientID(): string {
    return this.startupData.launchDarklyClientID;
  }
}
